import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Calendar, ChevronLeft, ChevronRight, Clock, Coffee, LogOut, 
  Loader2, User, AlertCircle 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '../../utils/apiClient';

/**
 * Staff Schedule Page
 * Allows staff to view their work schedule
 */
export default function StaffSchedulePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [schedules, setSchedules] = useState([]);
  const [staffInfo, setStaffInfo] = useState(null);
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const now = new Date();
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1); // Start from Monday
    return new Date(now.setDate(diff));
  });

  const token = localStorage.getItem('staff_token');

  useEffect(() => {
    if (!token) {
      navigate('/staff/login');
      return;
    }
    fetchStaffInfo();
    fetchSchedule();
  }, [token, navigate, currentWeekStart]);

  const fetchStaffInfo = async () => {
    try {
      const res = await api.get(`/staff-portal/profile?token=${token}`);
      setStaffInfo(res.data);
    } catch (err) {
      console.error('Failed to fetch staff info:', err);
    }
  };

  const fetchSchedule = async () => {
    setLoading(true);
    try {
      const weekEnd = new Date(currentWeekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      
      const res = await api.get(
        `/staff-portal/my-schedule?token=${token}&start_date=${currentWeekStart.toISOString().split('T')[0]}&end_date=${weekEnd.toISOString().split('T')[0]}`
      );
      setSchedules(res.data.schedules || []);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('staff_token');
        navigate('/staff/login');
      }
      console.error('Failed to fetch schedule:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('staff_token');
    localStorage.removeItem('staff_info');
    toast.success('Logged out');
    navigate('/staff/login');
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setCurrentWeekStart(newDate);
  };

  const getWeekDays = () => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(currentWeekStart);
      day.setDate(day.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const getShiftForDay = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return schedules.find(s => s.date === dateStr);
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const weekDays = getWeekDays();
  const weekEndDate = new Date(currentWeekStart);
  weekEndDate.setDate(weekEndDate.getDate() + 6);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/staff/profile" className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
              <ChevronLeft className="w-6 h-6" />
            </Link>
            <div>
              <h1 className="font-bold text-gray-900 dark:text-white">My Schedule</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">{staffInfo?.name || 'Staff'}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={handleLogout}>
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Week Navigation */}
        <div className="flex items-center justify-between mb-6">
          <Button variant="outline" size="sm" onClick={() => navigateWeek(-1)}>
            <ChevronLeft className="w-4 h-4 mr-1" />
            Previous
          </Button>
          <div className="text-center">
            <p className="font-semibold text-gray-900 dark:text-white">
              {currentWeekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {weekEndDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigateWeek(1)}>
            Next
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
          </div>
        ) : (
          <div className="space-y-3">
            {weekDays.map((day, index) => {
              const shift = getShiftForDay(day);
              const today = isToday(day);
              
              return (
                <div 
                  key={index}
                  className={`bg-white dark:bg-gray-800 rounded-xl border ${today ? 'border-teal-500 ring-2 ring-teal-500/20' : 'border-gray-200 dark:border-gray-700'} p-4`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-lg flex flex-col items-center justify-center ${today ? 'bg-teal-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`}>
                        <span className="text-xs font-medium">
                          {day.toLocaleDateString('en-US', { weekday: 'short' })}
                        </span>
                        <span className="text-lg font-bold">
                          {day.getDate()}
                        </span>
                      </div>
                      <div>
                        <p className={`font-medium ${today ? 'text-teal-600 dark:text-teal-400' : 'text-gray-900 dark:text-white'}`}>
                          {day.toLocaleDateString('en-US', { weekday: 'long' })}
                          {today && <span className="ml-2 text-xs bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 px-2 py-0.5 rounded-full">Today</span>}
                        </p>
                        {shift ? (
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {formatTime(shift.start_time)} - {formatTime(shift.end_time)}
                            {shift.position && <span className="ml-2 text-teal-600 dark:text-teal-400">• {shift.position}</span>}
                          </p>
                        ) : (
                          <p className="text-sm text-gray-400 dark:text-gray-500">No shift scheduled</p>
                        )}
                      </div>
                    </div>
                    {shift && (
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {shift.hours || '--'} hrs
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Summary */}
        <div className="mt-6 bg-teal-50 dark:bg-teal-900/20 rounded-xl p-4 border border-teal-200 dark:border-teal-800">
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-teal-600 dark:text-teal-400" />
            <div>
              <p className="font-medium text-teal-900 dark:text-teal-300">This Week</p>
              <p className="text-sm text-teal-700 dark:text-teal-400">
                {schedules.length} shift{schedules.length !== 1 ? 's' : ''} scheduled
                {schedules.length > 0 && ` • ${schedules.reduce((acc, s) => acc + (s.hours || 0), 0)} total hours`}
              </p>
            </div>
          </div>
        </div>

        {/* Info Note */}
        <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
          <div className="flex gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
            <p className="text-sm text-blue-700 dark:text-blue-400">
              Schedule changes are made by your manager. If you need to request time off or swap a shift, please use the Time Off request feature or speak with your manager.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
